<template>
    <v-container grid-list-xl fluid>
        <v-card class="mb-4">
            <div class="d-flex flex-wrap align-center pa-3">
                <v-avatar size="48" :color="`${status.color} lighten-4`" class="mr-3">
                    <v-icon :color="status.color">router</v-icon>
                </v-avatar>

                <div class="flex-grow-1 mr-3" style="min-width: 0">
                    <div class="d-flex align-center flex-wrap">
                        <span class="text-h6 text-truncate mr-2">{{ chargerNames || board }}</span>
                        <v-chip x-small label dark :color="status.color">
                            <v-icon left x-small>{{ status.icon }}</v-icon>{{ status.text }}
                        </v-chip>
                    </div>
                    <div class="caption grey--text">
                        Gateway <code>{{ board }}</code>
                    </div>
                </div>

                <div v-if="details" class="vpn mt-2 mt-sm-0">
                    <ButtonVPN :UUID="details.name" />
                </div>
            </div>

            <template v-if="details">
                <v-divider></v-divider>
                <div class="px-3 py-2">
                    <v-chip v-if="details.version" small label outlined class="mr-1 my-1" title="Versão do firmware">
                        <v-icon left small>local_offer</v-icon>{{ details.version }}
                    </v-chip>
                    <v-chip v-if="pendingVersion" small label color="orange" text-color="white" class="mr-1 my-1" title="Versão solicitada, aguardando o gateway atualizar">
                        <v-icon left small>system_update</v-icon>Atualizando para {{ pendingVersion }}
                    </v-chip>
                    <v-chip v-if="details.connection" small label outlined class="mr-1 my-1" title="Conexão">
                        <v-icon left small>{{ connectionIcon }}</v-icon>{{ details.connection }}
                    </v-chip>
                    <v-chip v-if="details.bootCounter > 0" small label outlined class="mr-1 my-1" title="Número de reinicializações">
                        <v-icon left small>restart_alt</v-icon>{{ details.bootCounter }} reboot{{ details.bootCounter > 1 ? "s" : "" }}
                    </v-chip>
                    <v-chip v-if="details.needRestart" small label color="orange" text-color="white" class="mr-1 my-1" title="Aguardando o gateway reiniciar">
                        <v-icon left small>pending</v-icon>Reinício solicitado
                    </v-chip>
                </div>
            </template>
        </v-card>

        <ExpandPanelSimple title="Configurações">
            <Form :itemToEdit="config" :lock="false" @save="init" controlAPI />
        </ExpandPanelSimple>
        <v-divider class="mb-4" />

        <template v-if="info">
            <v-alert v-for="(alarm, index) in alarms" :key="index" dense text :type="alarm.critical ? 'error' : 'warning'" class="mb-2">{{ alarm.text }}</v-alert>

            <GatewayDashboard :info="info" />

            <DiagnosticRS485 v-if="info.diagnostic && info.diagnostic.length > 0" :diagnostics="info.diagnostic" :chargers="info.chargers || {}" />

            <ExpandPanelSimple title="Dados completos (JSON)">
                <pre>{{ JSON.stringify(info, null, 2) }}</pre>
            </ExpandPanelSimple>
        </template>
    </v-container>
</template>

<script>
import { mapGetters } from "vuex";

import Form from "@/components/form/Board";
import ExpandPanelSimple from "@/components/core/ExpandPanelSimple.vue";
import ButtonVPN from "@/components/shared/ButtonVPN";
import DiagnosticRS485 from "@/components/shared/DiagnosticRS485";
import GatewayDashboard from "@/components/shared/GatewayDashboard";
import { boardAlarms } from "@/helpers/boardAlarms";

export default {
    name: "Charger",

    components: { Form, ExpandPanelSimple, ButtonVPN, DiagnosticRS485, GatewayDashboard },

    data() {
        return {
            isAdmin: false,
            details: null,
            board: null,
            config: null,
            timer: null,
        };
    },

    mounted() {
        this.isAdmin = this.verifyRole("ROLE_ADMIN");
        if (this.$route.params.name) {
            this.board = this.$route.params.name;
        }
        this.init();
        // Atualiza só os detalhes, o formulário de configuração não é recarregado para não perder edição
        this.timer = setInterval(this.updateDetails, 10000);
    },

    beforeDestroy() {
        clearInterval(this.timer);
    },

    computed: {
        ...mapGetters(["verifyRole"]),
        info() {
            try {
                return this.details && this.details.info ? JSON.parse(this.details.info) : undefined;
            } catch (e) {
                return undefined;
            }
        },
        status() {
            if (!this.details) return { color: "grey", icon: "hourglass_empty", text: "Carregando" };
            if (this.details.alarm || this.alarms.length > 0) return { color: "red", icon: "error", text: "Com alarme" };
            return { color: "green", icon: "check_circle", text: "Normal" };
        },
        // Nome dos carregadores configurados no gateway (link_name), o UUID fica como identificação secundária
        chargerNames() {
            const chargers = (this.info && this.info.chargers) || {};
            return Object.values(chargers)
                .map((charger) => (charger && (charger.link_name || charger.name)) || null)
                .filter((name) => name)
                .join(", ");
        },
        pendingVersion() {
            const { lastVersion, version } = this.details || {};
            if (!lastVersion || lastVersion == version) return null;
            return lastVersion == "latest" ? "última versão" : lastVersion;
        },
        connectionIcon() {
            const icons = { "RJ-45": "settings_ethernet", "Wi-Fi": "wifi", "4G": "signal_cellular_alt" };
            return icons[this.details.connection] || "cable";
        },
        // Mesmas regras usadas para marcar o gateway com alarme na lista de gateways com erro
        alarms() {
            return boardAlarms(this.details);
        },
    },

    methods: {
        updateDetails() {
            if (this.board) {
                this.$http
                    .get(`api/v3/board/details/${this.board}`, { continuous: true })
                    .then((result) => {
                        this.details = result;
                    })
                    .catch(() => {});
            }
        },
        async init() {
            if (this.board) {
                await this.$http
                    .get(`api/v3/board/details/${this.board}`)
                    .then((result) => {
                        this.details = result;
                    })
                    .catch(() => {
                        this.$eventHub.$emit("msgError", "Erro ao buscar informações.");
                    });
                if (this.details && this.details.id) {
                    this.config = null;
                    await this.$http
                        .get(`api/v2/board/${this.details.id}`)
                        .then((result) => {
                            this.config = result;
                        })
                }
            }
        },
    },
};
</script>

<style scoped>
.vpn {
    min-width: 200px;
}
</style>
